import React, { useCallback, useContext, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { LoaderArea, FlexColumn, InfiniteList } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import HelpDeskListEmpty from '../HelpDeskListEmpty';
import TicketListItem from './TicketListItem';

const ListWrapper = styled(FlexColumn)`
  flex: 1;
  min-height: 0;

  .react-virtualized-list {
    padding: 8px;
  }
`;

const LoadingMoreWrap = styled.div`
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const RowWrapper = styled.div`
  outline: none;
`;

// CellMeasurer measures each row once per cache key, so chips re-wrapping after an edit leave a stale
// height. The content is what gets observed -- the grid imposes a fixed height on RowWrapper itself.
const MeasuredRow = ({ measure, children }) => {
  const rowRef = useRef(null);

  useLayoutEffect(() => {
    const observer = new ResizeObserver(measure);
    observer.observe(rowRef.current);

    return () => observer.disconnect();
  }, [measure]);

  return <div ref={rowRef}>{children}</div>;
};

const HelpDeskTicketsList = ({
  tickets,
  isLoading,
  status,
  accountId,
  selectedTicketId,
  onTicketClick,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage
}) => {
  const { t } = useContext(ConfigContext);

  // CellMeasurerCache keyed by ticket id (not row index) so identical tickets keep their
  // cached heights across refetches — clicking a ticket invalidates the list query and the
  // refetched array reference changes, but the rows themselves haven't (same ids in same
  // order thanks to the deterministic backend sort tiebreaker). Without an id-based key,
  // every refetch would remeasure every visible row and the scroll position could shift by
  // a few pixels.
  const ticketsRef = useRef([]);
  const cache = useRef(
    new CellMeasurerCache({
      defaultHeight: 140,
      fixedWidth: true,
      keyMapper: (rowIndex) => ticketsRef.current[rowIndex]?.ticket?.id ?? rowIndex
    })
  );

  // Kept in sync (not via useEffect — needs to be available synchronously when the cache's
  // keyMapper is invoked during render) so the cache can look up the current id for an index.
  ticketsRef.current = tickets;

  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      if (index >= tickets.length) {
        return (
          <RowWrapper key={key} style={style}>
            <LoadingMoreWrap>{t('loading')}</LoadingMoreWrap>
          </RowWrapper>
        );
      }
      const item = tickets[index];
      const ticket = item.ticket ?? {};
      const isSelected = !!selectedTicketId && String(ticket.id) === String(selectedTicketId);

      const content = (
        <TicketListItem
          item={item}
          selected={isSelected}
          accountId={accountId}
          onClick={() => onTicketClick(ticket.id)}
        />
      );

      return (
        <CellMeasurer cache={cache.current} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          {({ measure, registerChild }) => (
            <RowWrapper ref={registerChild} style={style}>
              <MeasuredRow measure={measure}>{content}</MeasuredRow>
            </RowWrapper>
          )}
        </CellMeasurer>
      );
    },
    [tickets, selectedTicketId, t, onTicketClick, accountId]
  );

  if (isLoading) {
    return <LoaderArea />;
  }

  if (tickets.length === 0) {
    return <HelpDeskListEmpty status={status} />;
  }

  return (
    <ListWrapper>
      <AutoSizer>
        {({ width, height }) =>
          width > 0 && height > 0 ? (
            <InfiniteList
              rowCount={tickets.length + (hasNextPage ? 1 : 0)}
              width={width}
              height={height}
              className="react-virtualized-list"
              rowHeight={cache.current.rowHeight}
              deferredMeasurementCache={cache.current}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
              canFetchMore={hasNextPage && !isFetchingNextPage}
              fetchMore={fetchNextPage}
            />
          ) : null
        }
      </AutoSizer>
    </ListWrapper>
  );
};

export default HelpDeskTicketsList;
